/** "Split off and build its own tour…" (plan 2026-10-11 product-sync joins D2,
 *  §4a). A product whose key is an additional ID of another tour ‹T› is left
 *  out of a sync by default: syncing it would overwrite ‹T› with its content.
 *  Splitting it off makes the run build it as its own tour instead, and ‹T›
 *  stops using the key.
 *
 *  The confirmation is fed by the joins preview's `split_off` entry, so it
 *  states the server's facts: the bookings on ‹T› and the compound IDs that
 *  stay there. Confirming only records the consent in the run; the swap
 *  happens during the import.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment } from 'react';

import { isCatalogueNotLoaded } from '@/api/client';
import * as syncApi from '@/api/sync';

export interface SplitOffDialogProps {
  onClose: () => void;
  onConfirm: () => void;
  scope: { tour_operator_id: number; integration: string; account: string | null };
  /** The run's mapping policy. */
  policy: string | null;
  product: { id: string; name: string };
  /** The tour the product's key is an additional ID of now. */
  fromTour: { id: number; name: string | null };
  /** The run's selection; the product is added if it is not in it. */
  productIds: string[];
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function SplitOffDialog({
  onClose,
  onConfirm,
  scope,
  policy,
  product,
  fromTour,
  productIds,
}: SplitOffDialogProps) {
  const request = {
    ...scope,
    policy,
    product_ids: [...new Set([...productIds, product.id])],
    joins: [],
    split_off: [{ product_id: product.id, from_tour_id: fromTour.id }],
    booking_move: null,
  };
  const preview = useQuery({
    queryKey: ['sync', 'joins-preview', 'split-off', request],
    queryFn: ({ signal }) => syncApi.previewJoins(request, signal),
    retry: false,
    staleTime: 0,
  });
  const entry = preview.data?.split_off.find((s) => s.product_id === product.id);
  const tour = entry?.from_tour_name || fromTour.name || `tour #${fromTour.id}`;
  const key = entry?.key ?? product.id;
  const impact = entry?.impact.find((i) => i.tour_id === fromTour.id) ?? entry?.impact[0];
  const problems = entry?.problems ?? [];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Split {product.name} off {tour} and build its own tour?
      </DialogTitle>
      <DialogContent dividers>
        {preview.isPending && <LinearProgress aria-label="Checking the split-off" />}
        {preview.isError && (
          <Alert severity="warning">
            {isCatalogueNotLoaded(preview.error)
              ? 'The fetched catalogue is no longer cached. Close this dialog and fetch the products again.'
              : `The split-off could not be checked (${(preview.error as Error).message}).`}
          </Alert>
        )}
        {entry && (
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="body2">
              {tour} stops using <code>{key}</code>. A new tour is created from {product.name}
              &apos;s vendor data and keyed <code>{key}</code>.
            </Typography>
            <Typography component="li" variant="body2">
              {impact && impact.future_bookings > 0
                ? `${product.name}'s ${count(impact.future_bookings, 'future booking', 'future bookings')} on ${tour} ${impact.future_bookings === 1 ? 'moves' : 'move'} to the new tour only when the vendor next sends ${impact.future_bookings === 1 ? 'it; it loses its' : 'them; each loses its'} tags.`
                : `${product.name} has no future bookings on ${tour}.`}
            </Typography>
            {impact && impact.checked_in_future > 0 && (
              <Typography component="li" variant="body2">
                {count(impact.checked_in_future, 'checked-in booking stays', 'checked-in bookings stay')}{' '}
                on {tour}.
              </Typography>
            )}
            {impact && impact.past_bookings > 0 && (
              <Typography component="li" variant="body2">
                {count(impact.past_bookings, 'past booking stays', 'past bookings stay')} on {tour}.
              </Typography>
            )}
            {entry.compound_siblings.length > 0 && (
              <Typography component="li" variant="body2">
                {entry.compound_siblings.map((s, i) => (
                  <Fragment key={s}>
                    {i > 0 && ', '}
                    <code>{s}</code>
                  </Fragment>
                ))}{' '}
                {entry.compound_siblings.length === 1 ? 'stays' : 'stay'} on {tour} and keep
                {entry.compound_siblings.length === 1 ? 's' : ''} matching it. Remove{' '}
                {entry.compound_siblings.length === 1 ? 'it' : 'them'} in Mappings if{' '}
                {entry.compound_siblings.length === 1 ? 'it' : 'they'} should follow.
              </Typography>
            )}
          </Box>
        )}
        {problems.length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {problems.map((p, i) => (
              <div key={i}>{p.detail}</div>
            ))}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!entry || problems.length > 0}
          onClick={onConfirm}
        >
          Split off
        </Button>
      </DialogActions>
    </Dialog>
  );
}
